<script lang="ts">
  import { untrack } from 'svelte';
  import { toggleFavorite } from '$lib/favorite';
  import { addToast } from '$shared/toasts.svelte';
  import { isLoggedIn } from '$lib/auth.svelte';

  interface Props {
    trackId: number;
    initialFavorited?: boolean;
    size?: 'sm' | 'md';
  }

  let { trackId, initialFavorited = false, size = 'md' }: Props = $props();

  // `untrack` prevents $state from reactively mirroring the prop — the prop is
  // an initial seed only; post-mount the component owns the favorited state.
  let favorited = $state(untrack(() => initialFavorited));
  let loading = $state(false);

  async function toggle() {
    if (loading) return;
    if (!isLoggedIn()) {
      addToast('Log in to save tracks', 'join');
      return;
    }
    // Optimistic: flip state immediately, revert on error.
    const wasFavorited = favorited;
    favorited = !wasFavorited;
    loading = true;
    try {
      const next = await toggleFavorite(trackId, wasFavorited);
      favorited = next;
      addToast(next ? 'Track saved!' : 'Removed from saved', next ? 'follow' : 'join');
    } catch {
      favorited = wasFavorited;
      addToast('Could not update saved state', 'error');
    } finally {
      loading = false;
    }
  }
</script>

<button
  class="heart-btn"
  class:heart-btn--active={favorited}
  class:heart-btn--sm={size === 'sm'}
  onclick={toggle}
  disabled={loading}
  title={favorited ? 'Remove from saved' : 'Save to your catalog'}
  aria-pressed={favorited}
>
  {favorited ? '♥' : '♡'}
</button>

<style>
  .heart-btn {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 999px;
    color: var(--text-muted);
    cursor: pointer;
    font-size: 1.25rem;
    line-height: 1;
    padding: 0.5rem 0.75rem;
    transition:
      color 0.15s ease,
      border-color 0.15s ease,
      background 0.15s ease;
  }
  .heart-btn:hover:not(:disabled) {
    color: var(--pink);
    border-color: var(--pink);
  }
  .heart-btn:disabled {
    opacity: 0.5;
    cursor: wait;
  }
  .heart-btn--active {
    color: var(--pink);
    border-color: var(--pink);
  }
  .heart-btn--sm {
    font-size: 1rem;
    padding: 0.3rem 0.5rem;
  }
</style>
